/**
 * Add-to-cart click binding.
 *
 * Markup contract: any element carrying `data-amri-add-to-cart` AND
 * `data-product-type="<numeric ProductTypeEnum value>"` is treated as an
 * add-to-cart trigger. Server-rendered by the landing-courses partial:
 *
 *   <button
 *     class="amri-btn"
 *     data-amri-add-to-cart
 *     data-product-type="@((int)ProductTypeEnum.ACLSCertification)">
 *     Certification
 *   </button>
 *
 * Behavior:
 *   1. Click → POST `/umbraco/surface/checkoutsurface/addtocart` form-urlencoded
 *      with `productType=<value>`.
 *   2. On `{ success: true }`, call `refreshCartCount()` from `cart-counter`
 *      so both the desktop + mobile counter badges update.
 *   3. Toggle a transient `.amri-added` class on the clicked element for 2s as
 *      inline feedback (CSS can theme this — by default visible-feedback is
 *      a brief 100ms reset of `data-count` plus the next `refreshCartCount`).
 *   4. On `{ success: false }` or network failure, silently no-op. The cart
 *      counter is a non-critical widget; we don't surface error toasts here.
 *      A user-visible error message lands with the cart page in Phase 3.16.
 *
 * Click delegation is bound to `document` so the handler captures clicks on
 * markup that gets re-rendered after init (carousel slides). Idempotent —
 * `initAddToCart()` can be called multiple times.
 */

import { refreshCartCount } from './cart-counter';

const ENDPOINT = '/umbraco/surface/checkoutsurface/addtocart';
const HOOK = 'data-amri-add-to-cart';
const PRODUCT_TYPE_ATTR = 'data-product-type';
const FEEDBACK_CLASS = 'amri-added';
const FEEDBACK_MS = 2000;

interface AddToCartResponse {
    success: boolean;
    cartId?: number;
    count?: number;
}

async function addToCart(productType: number): Promise<AddToCartResponse | null> {
    const body = new URLSearchParams({ productType: String(productType) });
    try {
        const response = await fetch(ENDPOINT, {
            method: 'POST',
            credentials: 'same-origin',
            headers: {
                'Content-Type': 'application/x-www-form-urlencoded',
                Accept: 'application/json',
            },
            body: body.toString(),
        });
        if (!response.ok) {
            return null;
        }
        return (await response.json()) as AddToCartResponse;
    } catch {
        return null;
    }
}

function showAddedFeedback(target: HTMLElement): void {
    target.classList.add(FEEDBACK_CLASS);
    window.setTimeout(() => target.classList.remove(FEEDBACK_CLASS), FEEDBACK_MS);
}

function onClick(event: MouseEvent): void {
    const target = event.target;
    if (!(target instanceof HTMLElement)) {
        return;
    }

    const trigger = target.closest<HTMLElement>(`[${HOOK}]`);
    if (trigger === null) {
        return;
    }

    const raw = trigger.getAttribute(PRODUCT_TYPE_ATTR);
    const productType = raw === null ? Number.NaN : Number.parseInt(raw, 10);
    if (!Number.isFinite(productType)) {
        return;
    }

    event.preventDefault();
    void (async () => {
        const result = await addToCart(productType);
        if (result?.success === true) {
            showAddedFeedback(trigger);
            void refreshCartCount();
        }
    })();
}

let initialised = false;

export function initAddToCart(): void {
    if (initialised) {
        return;
    }
    initialised = true;
    document.addEventListener('click', onClick);
}
